import { Label } from 'cpool-components/general/Label';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { type APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useExpandedItem } from './context/ExpandedItemContext';
import type { SchemaCreditRequestForm } from '../schemas/creditRequestForm.schema';
import { LoanTypeKeys } from '../types';
import type { LoanType } from '../types';
import { discardUnsavedCredit } from './utils/discardUnsavedCredit';
import { getNewCredit } from './utils/getNewCredit';

type KreditTypeSelectProps = {
    selectedCreditType: LoanType | null;
    setSelectedCreditType: (creditType: LoanType | null) => void;
    defaultApplicant: APPLICANT_ATTRIBUTION_BOTH_TYPE;
    disabled?: boolean;
};

export const KreditTypeSelect = ({
    defaultApplicant,
    disabled,
    selectedCreditType,
    setSelectedCreditType,
}: KreditTypeSelectProps) => {
    const { t } = useTranslation();
    const { isExpandedItemDirty } = useExpandedItem();
    const { getValues, reset, setValue } = useFormContext<SchemaCreditRequestForm>();

    const selectCreditType = (creditType: LoanType) => {
        if (creditType === selectedCreditType) return;
        if (isExpandedItemDirty()) {
            toast.warn(t('CREDIT_WORTHINESS.TOASTS.DIRTY'));
            return;
        }
        if (selectedCreditType) discardUnsavedCredit({ creditType: selectedCreditType, getValues, reset, setValue });
        setSelectedCreditType(creditType);

        const creditTypeValues = getValues(creditType) ?? [];
        if (creditTypeValues.some((credit) => credit.id === '')) return;

        setValue(
            `${creditType}.${creditTypeValues.length}`,
            getNewCredit({ creditType, selectedApplicant: defaultApplicant }),
        );
    };

    return (
        <div className="flex items-center gap-2.5">
            <Label id="refinance-planner-credit-type" className="h-8 shrink-0 justify-start">
                {t('BANK_CREDIT_REQUEST.REFINANCE_PLANNER.SELECT_LOAN_TYPE')}:
            </Label>
            <DropdownMenu<LoanType>
                aria-labelledby="refinance-planner-credit-type"
                data-testid="refinance-planner-credit-type"
                options={LoanTypeKeys.map((value) => ({ label: t(`EXISTING_LOANS.TYPES.${value}`), value }))}
                value={selectedCreditType ?? undefined}
                placeholder={t('SELECT_PLACEHOLDER')}
                onChange={(value) => selectCreditType(value as LoanType)}
                disabled={disabled}
            />
        </div>
    );
};
